import React, { useEffect, useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Grid, Typography } from "@mui/material";
import { MODES_OPTIONS } from "@app/types/generics.types";
import { useInstallation } from "@services/servicesGenericsTables";
import {
  MAutocomplete,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { errorAlert, successAlert } from "../../../../utils/alerts";

dayjs.extend(isoWeek);

interface Anexo2FormProps {
  mode: MODES_OPTIONS;
  disabled?: boolean;
}

const Anexo2Form = ({ mode, disabled }: Anexo2FormProps) => {
  const { watch, setValue, getValues } = useFormContext();
  const { installationList } = useInstallation();
  const isView = mode === MODES_OPTIONS.VIEW;

  const [maxDate, setMaxDate] = useState<Date | undefined>(undefined);
  const [minDate, setMinDate] = useState<Date | undefined>(undefined);

  const evo_start_date = getValues("evo_start_date");
  const evo_end_date = getValues("evo_end_date");
  const year_pickup = watch("year_pickup");
  const start_week_pickup = watch("start_week_pickup");
  const end_week_pickup = watch("end_week_pickup");

  useEffect(() => {
    if (evo_start_date) {
      const minDateObj = dayjs(evo_start_date).toDate();
      setMinDate(minDateObj);
    }
    if (evo_end_date) {
      const maxDateObj = dayjs(evo_end_date).toDate();
      setMaxDate(maxDateObj);
    }
  }, [evo_start_date, evo_end_date]);

  useEffect(() => {
    if (year_pickup && start_week_pickup) {
      const startDate = dayjs()
        .year(year_pickup)
        .isoWeek(start_week_pickup)
        .startOf("isoWeek")
        .toDate();
      setMinDate(startDate);
    }

    if (year_pickup && end_week_pickup) {
      const endDate = dayjs()
        .year(year_pickup)
        .isoWeek(end_week_pickup)
        .endOf("isoWeek")
        .toDate();
      setMaxDate(endDate);
    }
  }, [year_pickup, start_week_pickup, end_week_pickup]);

  const watchInstallation = watch("report_installation");

  const formatDay = (date: string | null, textMessage: string) => {
    return date ? dayjs(date).format("DD/MM/YYYY").toString() : textMessage;
  };

  const updateInstallationFields = useCallback(() => {
    if (watchInstallation) {

      if (!watchInstallation.fecha_inicio && !watchInstallation.fecha_limite && !isView) {        
        errorAlert("La instalación elegida no posee fechas límite para el Anexo II. Desmarque dicha opción o elija otra instalación.");
      }

      setValue("evo_installation", watchInstallation);
      setValue(
        "operation_start_date",
        formatDay(
          watchInstallation.fecha_inicio,
          "No tiene fecha de In. Operación"
        )
      );
      setValue(
        "operation_limit_date",
        formatDay(
          watchInstallation.fecha_limite,
          "No tiene fecha de Lim. Operación"
        )
      );
    } else {
      setValue("evo_installation", null);
      setValue("operation_start_date", undefined);
      setValue("operation_limit_date", undefined);
    }
  }, [watchInstallation, setValue]);

  useEffect(() => {
    updateInstallationFields();
  }, [watchInstallation, updateInstallationFields]);

  return (
    <>
      <Grid item xs={12} my={2}>
        <Typography variant="tagText">{"Anexo II"}</Typography>
      </Grid>
      <ResponsiveFormContainer columns={5}>
        <MAutocomplete
          key={JSON.stringify(getValues("evo_installation"))}
          name="evo_installation"
          options={installationList}
          label="Instalación"
          getOptionLabel={(option) =>
            `${option.codigo} - ${option.descripcion}`
          }
          validation={{ required: "La instalación es requerida" }}
          disabled
        />

        <MTextField
          key={JSON.stringify(getValues("operation_start_date"))}
          name="operation_start_date"
          label="Fecha In. operación"
          placeholder="Fecha In. operación"
          fullWidth
          disabled
        />
        <MTextField
          key={JSON.stringify(watch("operation_limit_date"))}
          name="operation_limit_date"
          label="Fecha Lim. operación"
          placeholder="Fecha Lim. operación"
          fullWidth
          disabled
        />
      </ResponsiveFormContainer>
    </>
  );
};

export default Anexo2Form;
